import { UserSession, UserSessionUnit } from '../../interface/users.interface';
import { CurrentUser } from '../../interface/currentUser.interface';

/**
 * VDS-3387 — merge canonical monorepo user-session into the dashboard snapshot.
 * Preserves auth fields (fbToken, sessionGrant, features, etc.) and refreshes
 * building id / unit / roles used by isProprietorOnly and buildingId headers.
 */
export function mergeUserSessionIntoCurrentUser(
  current: Partial<CurrentUser>,
  session: UserSession,
  preferredBuildingId?: string,
): Partial<CurrentUser> {
  if (!current || !session?.units?.length) {
    return current;
  }

  const preferred =
    (preferredBuildingId &&
      session.units.find((unit) => unit.building?.id === preferredBuildingId)) ||
    session.units[0];

  if (!preferred?.building?.id) {
    return current;
  }

  const previousBuilding = current.buildings?.[0] ?? {};
  const nextBuilding = mapSessionUnitToBuildingSnapshot(previousBuilding, preferred);
  const sessionRoles = Array.isArray(preferred.roles) ? preferred.roles : [];
  const currentRoles = Array.isArray(current.roles) ? current.roles : [];
  const roles = sessionRoles.length > 0 ? [...sessionRoles] : [...currentRoles];

  return {
    ...current,
    roles,
    buildings: [nextBuilding],
  };
}

function mapSessionUnitToBuildingSnapshot(
  previousBuilding: Record<string, unknown>,
  unit: UserSessionUnit,
): Record<string, unknown> {
  const buildingId = unit.building.id;
  return {
    ...previousBuilding,
    id: buildingId,
    _id: buildingId,
    name: unit.building.name ?? previousBuilding.name,
    status: unit.building.status ?? previousBuilding.status,
    ico: unit.building.ico ?? previousBuilding.ico,
    roles:
      Array.isArray(unit.roles) && unit.roles.length > 0
        ? [...unit.roles]
        : previousBuilding.roles,
    unitId: unit.id,
    unit: {
      id: unit.id,
      number: unit.number,
    },
    unitNumber: unit.number,
  };
}
